import { charValueAt, stripDocumentSeparators } from './internal';

/** CNPJ canônico (IN RFB nº 2.229/2024): 12 alfanuméricos maiúsculos + 2 dígitos verificadores. */
export const CNPJ_PATTERN = /^[A-Z0-9]{12}[0-9]{2}$/;
export const CNPJ_LENGTH = 14;

/** Letras que a norma recomenda evitar (confundem com dígitos). Não invalidam: só avisam. */
export const CNPJ_DISCOURAGED_LETTERS: readonly string[] = Object.freeze(['I', 'O', 'Q', 'F']);

export type CnpjWarningCode = 'discouraged-letters';

export interface CnpjWarning {
  readonly code: CnpjWarningCode;
  readonly letters: readonly string[];
}

export type CnpjErrorCode =
  | 'empty'
  | 'invalid-length'
  | 'invalid-characters'
  | 'repeated-digits'
  | 'check-digit-mismatch';

export interface CnpjValidation {
  readonly valid: boolean;
  readonly canonical: string | null;
  readonly error: CnpjErrorCode | null;
  readonly warnings: readonly CnpjWarning[];
}

const FIRST_WEIGHTS: readonly number[] = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const SECOND_WEIGHTS: readonly number[] = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const NO_WARNINGS: readonly CnpjWarning[] = Object.freeze([]);
// U14.1: o Front aceitava `00000000000000` (a aritmética fecha); o Framework rejeita.
const REPEATED_DIGITS = /^([0-9])\1{13}$/;

function checkDigit(base: string, weights: readonly number[]): number {
  let sum = 0;
  weights.forEach((weight, index) => {
    sum += charValueAt(base, index) * weight;
  });
  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
}

/** Os dois dígitos verificadores a partir dos 12 primeiros caracteres; `null` se a base é inválida. */
export function computeCnpjCheckDigits(base: string): string | null {
  if (!/^[A-Z0-9]{12}$/.test(base)) return null;
  const first = checkDigit(base, FIRST_WEIGHTS);
  const second = checkDigit(`${base}${String(first)}`, SECOND_WEIGHTS);
  return `${String(first)}${String(second)}`;
}

/** Forma canônica (14 caracteres, maiúsculos) ou `null`. Só forma: não confere dígito verificador. */
export function canonicalizeCnpj(input: string): string | null {
  const stripped = stripDocumentSeparators(input.toUpperCase());
  return CNPJ_PATTERN.test(stripped) ? stripped : null;
}

export function cnpjWarnings(canonical: string): readonly CnpjWarning[] {
  const letters = CNPJ_DISCOURAGED_LETTERS.filter((letter) => canonical.includes(letter));
  return letters.length === 0 ? NO_WARNINGS : [{ code: 'discouraged-letters', letters }];
}

/** Valida CNPJ numérico ou alfanumérico (uma implementação só) e canonicaliza. */
export function validateCnpj(input: string): CnpjValidation {
  const stripped = stripDocumentSeparators(input.toUpperCase());
  const fail = (error: CnpjErrorCode): CnpjValidation => ({
    valid: false,
    canonical: null,
    error,
    warnings: NO_WARNINGS,
  });
  if (stripped.length === 0) return fail('empty');
  if (stripped.length !== CNPJ_LENGTH) return fail('invalid-length');
  if (!CNPJ_PATTERN.test(stripped)) return fail('invalid-characters');
  if (REPEATED_DIGITS.test(stripped)) return fail('repeated-digits');
  const expected = computeCnpjCheckDigits(stripped.slice(0, 12));
  if (expected === null || stripped.slice(12) !== expected) return fail('check-digit-mismatch');
  return { valid: true, canonical: stripped, error: null, warnings: cnpjWarnings(stripped) };
}

export function isValidCnpj(input: string): boolean {
  return validateCnpj(input).valid;
}

/** `12.ABC.345/01DE-35` para exibição, ou `null` se o CNPJ é inválido. */
export function formatCnpj(input: string): string | null {
  const { canonical } = validateCnpj(input);
  if (canonical === null) return null;
  return (
    `${canonical.slice(0, 2)}.${canonical.slice(2, 5)}.${canonical.slice(5, 8)}` +
    `/${canonical.slice(8, 12)}-${canonical.slice(12)}`
  );
}
